// src/lib/crumbs.tsx
//
// ★ WHAT A SCREEN CALLS ITSELF, once, for three readers: the breadcrumb, the browser tab and a screen reader's
// "page title" announcement (UAT re-run F-08, F-22).
//
// ⚠ A URL carries an opaque id ("/administration/mso-rules/versions/<uuid>"); only the page knows that it is
// "MSO-R1 v2". A page that knows its own name registers it with `useCrumbTitle`, and the shell draws it as the last
// crumb and in `document.title`. A page that does not register one still gets the label its route is registered
// under (`CRUMB_LABEL` in Shell.tsx), so nothing here is required of a page.
import { createContext, useContext, useEffect } from 'react'
import { useLocation } from 'react-router'

/** The product name that ends every page title: "MSO calculator | PRS2.0". A bar, never a dash. */
export const TITLE_SUFFIX = 'PRS2.0'

export interface CrumbRegistration {
  /** The path the title was registered for: a title for a screen the officer has left is never drawn. */
  readonly path: string
  readonly label: string
}

export interface CrumbContextValue {
  readonly set: (registration: CrumbRegistration | null) => void
}

export const CrumbContext = createContext<CrumbContextValue | null>(null)

/** The words in the browser tab for a screen: "<screen> | PRS2.0". */
export const pageTitle = (screen: string): string => `${screen} | ${TITLE_SUFFIX}`

/** Name the current screen (last breadcrumb, tab title). Pass `null` while the name is not yet known. */
export function useCrumbTitle(label: string | null): void {
  const context = useContext(CrumbContext)
  const { pathname } = useLocation()
  useEffect(() => {
    if (context === null || label === null) return
    context.set({ path: pathname, label })
    return () => context.set(null)
  }, [context, label, pathname])
}

/** Set the tab title of a screen outside the shell (sign-in, the legal notices). */
export function useDocumentTitle(screen: string): void {
  useEffect(() => {
    document.title = pageTitle(screen)
  }, [screen])
}
